import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import Input from '@/components/common/Input';
import PasswordInput from '@/components/common/PasswordInput';
import Button from '@/components/common/Button';
import SocialLoginButtons from '@/components/common/SocialLoginButtons';
import { validatePassword, getPasswordStrength } from '@/utils/validation';

export default function SignupPage() {
  const { signUp, signInWithGoogle, confirmSignUp, error, clearError } = useAuth();
  const [step, setStep] = useState<'register' | 'verify'>('register');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const passwordValidation = validatePassword(password);
  const passwordStrength = getPasswordStrength(password);

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();

    if (password !== confirmPassword) {
      return;
    }

    setIsSubmitting(true);
    try {
      await signUp(email, password, displayName);
      setStep('verify');
    } catch {
      // Error is set in the hook
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setIsSubmitting(true);
    try {
      await confirmSignUp(email, verificationCode);
    } catch {
      // Error is set in the hook
    } finally {
      setIsSubmitting(false);
    }
  };

  if (step === 'verify') {
    return (
      <form onSubmit={handleVerify} className="space-y-4">
        <h2 className="text-xl font-semibold text-neutral-900">Verify Your Email</h2>
        <p className="text-sm text-neutral-600">
          We sent a verification code to <strong>{email}</strong>. Enter it below.
        </p>

        {error && (
          <div className="rounded-lg bg-error-50 p-3 text-sm text-error-700" role="alert">
            {error}
          </div>
        )}

        <Input
          label="Verification Code"
          value={verificationCode}
          onChange={(e) => setVerificationCode(e.target.value)}
          placeholder=""
          required
          autoComplete="one-time-code"
        />

        <Button type="submit" className="w-full" loading={isSubmitting}>
          Verify Email
        </Button>
      </form>
    );
  }

  return (
    <form onSubmit={handleRegister} className="space-y-4">
      <h2 className="text-xl font-semibold text-neutral-900">Create Account</h2>

      {error && (
        <div className="rounded-lg bg-error-50 p-3 text-sm text-error-700" role="alert">
          {error}
        </div>
      )}

      <SocialLoginButtons onGoogleClick={signInWithGoogle} label="signup" />

      <Input
        label="Display Name"
        value={displayName}
        onChange={(e) => setDisplayName(e.target.value)}
        placeholder="Your name"
        required
      />

      <Input
        label="Email"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="you@example.com"
        required
        autoComplete="email"
      />

      <div>
        <PasswordInput
          label="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder=""
          required
          autoComplete="new-password"
          error={password && !passwordValidation.isValid ? passwordValidation.errors[0] : undefined}
        />
        {password && (
          <div className="mt-2">
            <div className="h-1.5 overflow-hidden rounded-full bg-neutral-300">
              <div
                className={`h-full rounded-full transition-all ${
                  passwordStrength < 40
                    ? 'bg-error-500'
                    : passwordStrength < 70
                      ? 'bg-warning-500'
                      : 'bg-success-500'
                }`}
                style={{ width: `${passwordStrength}%` }}
              />
            </div>
            <p className="mt-1 text-xs text-neutral-500">
              {passwordStrength < 40 ? 'Weak' : passwordStrength < 70 ? 'Fair' : 'Strong'}
            </p>
          </div>
        )}
      </div>

      <PasswordInput
        label="Confirm Password"
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        placeholder=""
        required
        autoComplete="new-password"
        error={confirmPassword && password !== confirmPassword ? 'Passwords do not match' : undefined}
      />

      <div className="flex items-start gap-2">
        <input
          id="agree-terms"
          type="checkbox"
          checked={agreedToTerms}
          onChange={(e) => setAgreedToTerms(e.target.checked)}
          className="mt-0.5 h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
        />
        <label htmlFor="agree-terms" className="text-sm text-neutral-600">
          I agree to the{' '}
          <a
            href="/terms-of-service"
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-primary-600 hover:text-primary-700"
          >
            Terms of Service
          </a>{' '}
          and{' '}
          <a
            href="/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-primary-600 hover:text-primary-700"
          >
            Privacy Policy
          </a>
        </label>
      </div>

      <Button
        type="submit"
        className="w-full"
        loading={isSubmitting}
        disabled={!passwordValidation.isValid || password !== confirmPassword || !agreedToTerms}
      >
        Create Account
      </Button>

      <p className="text-center text-sm text-neutral-600">
        Already have an account?{' '}
        <Link to="/login" className="font-medium text-primary-600 hover:text-primary-700">
          Sign in
        </Link>
      </p>
    </form>
  );
}
